import React, { useState, useRef, useEffect } from "react";
import {
    Box,
    Paper,
    IconButton,
    Typography,
    TextField,
    Button,
    Avatar,
    CircularProgress,
    Collapse,
    Stack,
    Fab
} from "@mui/material";
import {
    SmartToy,
    Send,
    Close,
    ExpandLess,
    ExpandMore,
    SupportAgent,
    AdminPanelSettings
} from "@mui/icons-material";
import styled, { keyframes, css } from "styled-components";
import firebase from "firebase/app";
import "firebase/functions";
import { connect } from "react-redux";

// --- Styled Components ---

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-3px); }
  100% { transform: translateY(0px); }
`;

const ChatContainer = styled(Paper)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 380px;
  max-width: 90vw;
  height: 600px;
  max-height: 80vh;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow: 0 20px 50px rgba(0,0,0,0.15);
  z-index: 1300;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', sans-serif;
  transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
  transform-origin: bottom right;

  ${props => !props.isOpen && css`
    opacity: 0;
    transform: scale(0.8) translateY(20px);
    pointer-events: none;
  `}
`;

const Header = styled(Box)`
  padding: 16px 20px;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255,255,255,0.1);
`;

const MessageList = styled(Box)`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background: #f8fafc;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const MessageBubble = styled(Box)`
  max-width: 85%;
  padding: 12px 16px;
  border-radius: 16px;
  font-size: 0.9rem;
  line-height: 1.5;
  position: relative;
  word-wrap: break-word;

  ${props => props.isUser ? css`
    align-self: flex-end;
    background: #0f172a;
    color: white;
    border-bottom-right-radius: 4px;
  ` : css`
    align-self: flex-start;
    background: white;
    color: #334155;
    border: 1px solid #e2e8f0;
    border-bottom-left-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.02);
  `}
`;

const InputArea = styled(Box)`
  padding: 16px;
  background: white;
  border-top: 1px solid #e2e8f0;
  display: flex;
  gap: 8px;
  align-items: center;
`;

const FloatingTrigger = styled(Fab)`
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  background: #0f172a !important;
  color: #bfd56c !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.4) !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    transform: scale(1.05) translateY(-2px);
    background: #1e293b !important;
  }

  animation: ${float} 3s ease-in-out infinite;
`;

const AdminAiAssistant = ({ profile, isOpen: controlledOpen, setIsOpen: setControlledOpen }) => {
    const [internalOpen, setInternalOpen] = useState(false);

    // Use controlled state if provided, otherwise internal
    const isControlled = controlledOpen !== undefined;
    const isOpen = isControlled ? controlledOpen : internalOpen;
    const setIsOpen = isControlled ? setControlledOpen : setInternalOpen;

    const [userMessage, setUserMessage] = useState("");
    const [loading, setLoading] = useState(false);
    const [customPrompt, setCustomPrompt] = useState(null);
    const [messages, setMessages] = useState([
        { role: 'ai', content: "Hello Admin! I am your Ellipse Assistant. How can I help you manage the platform today?" }
    ]);
    const messagesEndRef = useRef(null);

    // Auto-scroll
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isOpen]);

    // Fetch Custom Prompt Knowledge Base
    useEffect(() => {
        const fetchPrompt = async () => {
            try {
                const doc = await firebase.firestore().collection("aiPromptTemplates").doc("default").get();
                if (doc.exists && doc.data().promptAdmin) {
                    setCustomPrompt(doc.data().promptAdmin);
                }
            } catch (err) {
                console.error("Failed to load AI Admin Prompt", err);
            }
        };
        fetchPrompt();
    }, []);

    const handleSendMessage = async () => {
        if (!userMessage.trim() || loading) return;

        const currentMsg = userMessage;
        setUserMessage("");
        setMessages(prev => [...prev, { role: 'user', content: currentMsg }]);
        setLoading(true);

        try {
            const functions = firebase.app().functions("us-central1");
            const callAiCoach = functions.httpsCallable("callAiCoach");

            // Use Custom Prompt from Knowledge Base if available, else Fallback
            const defaultSystemInstruction = `You are an expert Admin Assistant for the Ellipse Platform (English Learning Platform).
      Your goal is to help administrators manage students (members), coaches, sprints, and payments.
      
      --- BUSINESS RULES & KNOWLEDGE BASE ---
      
      1. PRICING & CONTRACTS (Hourly Rates):
         - 1-9 hours: $110/h
         - 10-19 hours: $99/h
         - 20-29 hours: $89/h
         - 30-39 hours: $79/h
         - 40+ hours: $74/h
         - Sprint Membership (4 weeks): $1495 flat fee.
         - Hybrid Pack: Sprint ($1495) + Private Hours (Rate depends on quantity).
      
      2. SPRINT RULES:
         - A1 (Beginner) Difficulty: Members with A1 level are NOT allowed in Group Sprints. They must buy Private Hours.
         - Sprint Schedule: Morning (Start Strong), Afternoon (Keep Focus), Evening (Wrap Up).
         - Holidays: Sprints pause during defined holiday breaks.
      
      3. PLATFORM NAVIGATION:
         - To add a member: Go to 'Member Search' -> Click 'Add New'.
         - To check hours/balance: Go to 'Member Search' -> Find Member -> Check 'Hours Balance' or Open 'Member Admin'.
         - To assign a coach: Open Member Profile -> 'Member Admin' -> 'Assign Coaches'.
         - To generate contracts: Go to 'Global Admin' -> 'Contract Generator'.
         - To manage Sprints: Go to 'Global Admin' -> 'Sprint Admin'.
      
      4. TERMINOLOGY:
         - Members = Students/Learners.
         - Coaches = Professors/Teachers.
         - Learning Logs = Session notes.
      
      --- INSTRUCTIONS ---
      Be concise, professional, and action-oriented. 
      If asked about pricing, use the rules above.
      If asked about procedures, use the navigation guide.
      Always reply in the same language as the user (English or French).`;

            const systemInstruction = customPrompt || defaultSystemInstruction;

            const response = await callAiCoach({
                systemInstruction,
                userPrompt: currentMsg
            });

            const aiText = response.data?.text || "I'm sorry, I couldn't process that request.";
            setMessages(prev => [...prev, { role: 'ai', content: aiText }]);

        } catch (error) {
            console.error("AI Admin Error:", error);
            setMessages(prev => [...prev, { role: 'ai', content: "Sorry, I encountered an error connecting to the server." }]);
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    return (
        <>
            {/* Trigger Button */}
            {!isOpen && (
                <FloatingTrigger onClick={() => setIsOpen(true)}>
                    <SupportAgent sx={{ fontSize: 28 }} />
                </FloatingTrigger>
            )}

            {/* Chat Window */}
            <ChatContainer isOpen={isOpen} elevation={24}>
                <Header>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <Avatar sx={{ bgcolor: '#bfd56c', color: '#0f172a', width: 32, height: 32 }}>
                            <SmartToy sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box>
                            <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>Admin Copilot</Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>Powered by Ellipse AI</Typography>
                        </Box>
                    </Stack>
                    <IconButton onClick={() => setIsOpen(false)} size="small" sx={{ color: 'white' }}>
                        <ExpandMore />
                    </IconButton>
                </Header>

                <MessageList>
                    {messages.map((msg, i) => (
                        <MessageBubble key={i} isUser={msg.role === 'user'}>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Typography>
                        </MessageBubble>
                    ))}
                    {loading && (
                        <MessageBubble isUser={false}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <CircularProgress size={16} sx={{ color: '#0f172a' }} />
                                <Typography variant="caption" color="textSecondary">Thinking...</Typography>
                            </Stack>
                        </MessageBubble>
                    )}
                    <div ref={messagesEndRef} />
                </MessageList>

                <InputArea>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="How can I help you today?"
                        value={userMessage}
                        onChange={(e) => setUserMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        multiline
                        maxRows={3}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                bgcolor: '#f1f5f9',
                                '& fieldset': { border: 'none' }
                            }
                        }}
                    />
                    <IconButton
                        onClick={handleSendMessage}
                        disabled={!userMessage.trim() || loading}
                        sx={{
                            bgcolor: '#0f172a',
                            color: '#bfd56c',
                            '&:hover': { bgcolor: '#1e293b' },
                            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' }
                        }}
                    >
                        <Send fontSize="small" />
                    </IconButton>
                </InputArea>
            </ChatContainer>
        </>
    );
};

export default AdminAiAssistant;
